import { useCallback, useRef } from 'react'
import { OverlayContainer, useOverlayPosition, useOverlayTrigger } from '@react-aria/overlays'
import { useOverlayTriggerState } from '@react-stately/overlays'
import dayjs from 'dayjs'
import { Text } from 'blaze-ui/Text/Text'
import { ChevronDown } from 'blaze-ui/icons'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { Popover } from 'components/Popover/Popover'
import { CampaignOption } from 'types'
import { CampaignDropdown } from './CampaignDropdown'
import Styles from './CampaignChipDropdown.module.scss'

interface CampaignChipDropdownProps {
  schedules: CampaignOption[]
  selectedSchedule?: CampaignOption
  isLoading: boolean
  onSelect: (id: number) => void
  oneOffScheduleId?: number
}

export const CampaignChipDropdown = ({
  schedules,
  selectedSchedule,
  isLoading,
  onSelect,
  oneOffScheduleId,
}: CampaignChipDropdownProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)
  const overlayState = useOverlayTriggerState({ defaultOpen: false })
  const { overlayProps } = useOverlayTrigger({ type: 'dialog' }, overlayState, triggerRef)
  const { overlayProps: positionProps } = useOverlayPosition({
    targetRef: triggerRef,
    overlayRef,
    placement: 'bottom end',
    offset: 8,
    isOpen: overlayState.isOpen,
  })

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      overlayState.toggle()
    },
    [overlayState],
  )

  const isOneOff = selectedSchedule?.kind === 'one_off'

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={Styles.chipTrigger}
        onClick={handleToggle}
        aria-haspopup="dialog"
        aria-expanded={overlayState.isOpen}
      >
        <Text variant="secondary" color="var(--dark-60)">
          Campaign
        </Text>
        <div className={Styles.chipBox}>
          {selectedSchedule && (isOneOff ? <OneOffChipContent /> : <ChipContent schedule={selectedSchedule} />)}
          {!selectedSchedule && isLoading && <LoadingShimmerer className={Styles.chipShimmer} borderRadius={4} />}
          <ChevronDown size={12} />
        </div>
      </button>
      {overlayState.isOpen && (
        <OverlayContainer>
          <Popover
            {...overlayProps}
            {...positionProps}
            ref={overlayRef}
            isOpen={overlayState.isOpen}
            onClose={overlayState.close}
            isDismissable
            backdropClass={Styles.dropdownBackdrop}
            shouldCloseOnInteractOutside={(target) => !triggerRef.current?.contains(target as Node)}
            className={Styles.dropdownPopover}
          >
            <CampaignDropdown
              schedules={schedules}
              selectedScheduleId={selectedSchedule?.id}
              isLoading={isLoading}
              oneOffScheduleId={oneOffScheduleId}
              onSelect={(id) => {
                onSelect(id)
                overlayState.close()
              }}
            />
          </Popover>
        </OverlayContainer>
      )}
    </>
  )
}

const OneOffChipContent = () => (
  <div className={Styles.chipContent}>
    <Text variant="secondary" color="var(--dark-90)" lineClamp={1}>
      No Campaign
    </Text>
  </div>
)

const ChipContent = ({ schedule }: { schedule: CampaignOption }) => {
  const start = dayjs(schedule.start_date).format('MMM D')
  const end = dayjs(schedule.end_date).format('MMM D')

  return (
    <div className={Styles.chipContent}>
      <Text variant="secondary" color="var(--dark-90)" lineClamp={1}>
        {schedule.title ?? 'Untitled Campaign'}
      </Text>
      <div className={Styles.chipMeta}>
        <Text variant="label" color="var(--dark-15)">
          •
        </Text>
        <Text variant="secondary" color="var(--dark-60)">
          {start}
          <span className={Styles.chipDateDash}>–</span>
          {end}
        </Text>
      </div>
    </div>
  )
}
